import { faCheck, faMinus } from '@fortawesome/pro-solid-svg-icons';
import {
    ChangeEvent,
    ChangeEventHandler,
    forwardRef,
    HTMLProps,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import styled from 'styled-components';
import * as theme from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';
import { mergeRefs } from 'utils/mergeRefs';
import { buildSVG } from 'utils/svg';

const checkedSVG = buildSVG(faCheck);
const indeterminateSVG = buildSVG(faMinus);

const CheckboxLabel = styled.label`
    display: inline-flex;
    gap: 0.4em;
    cursor: pointer;
    align-items: center;
    flex-grow: 1;
`;

export type CheckboxValue = boolean | 'indeterminate';

const StyledCheckbox = styled.input<{
    $hidden?: boolean;
    $indeterminate?: 'true';
}>`
    line-height: 1.6rem;
    border-radius: 2px;
    width: 16px;
    height: 16px;
    box-sizing: border-box;
    border-width: 2px;
    border-style: solid;
    accent-color: ${KCFColor.checkbox};

    border-color: ${theme.navBorderColor};
    background: transparent;
    appearance: none;
    cursor: pointer;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        transition: none;
        width: 12px;
        height: 12px;
        display: block;
    }

    &:indeterminate:checked {
        // making the judgement call that this shouldn't be a thing you do
        background-color: red;
    }

    &:indeterminate {
        border-color: ${KCFColor.mediumGrey4};
        background: ${KCFColor.mediumGrey4};

        &::before {
            content: '';
            background-image: url('data:image/svg+xml;utf8,${indeterminateSVG}');
            transform: translateX(0.5px);
        }
    }

    &:checked {
        border-color: ${KCFColor.checkbox};
        background: ${KCFColor.checkbox};

        &::before {
            content: '';
            background-image: url('data:image/svg+xml;utf8,${checkedSVG}');
            transform: translateX(0.65px);
        }
    }

    &:hover,
    &:focus-visible {
        &:not(:disabled) {
            border-color: ${KCFColor.checkbox};
        }
    }

    &:disabled {
        filter: grayscale();
        cursor: default;
        &:not(:checked, :indeterminate) {
            border-color: ${theme.navBorderColor};
        }
    }

    ${(props) => (props.$hidden ? 'display: none;' : '')};
`;

export type CheckboxChangeEvent = {
    /**
     * The new value of the Checkbox.
     */
    value: boolean;
    /**
     * A React Synthetic Event.
     */
    syntheticEvent: ChangeEvent<HTMLInputElement>;
    // copying what kendo did for now. Is hard to be specific.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nativeEvent: any;
};

export type CheckboxOnlyProps = {
    checked?: CheckboxValue;
    defaultChecked?: boolean;
    disabled?: boolean;
    className?: string;
    hidden?: boolean;
    id?: string;
    /**
     * Sets the `tabIndex` property of the Checkbox.
     * Defaults to `0`.
     */
    tabIndex?: number;
    /**
     * The event handler that will be fired when the user edits the value.
     */
    onChange?: (event: CheckboxChangeEvent) => void;
    /**
     * The event handler that will be fired when Checkbox is focused.
     */
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    /**
     * The event handler that will be fired when Checkbox is blurred.
     */
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
};

type CheckboxLabelProps = {
    /**
     * Sets the label of the Checkbox component
     */
    label?: ReactNode;
    /**
     * Sets the label position of the Checkbox component
     */
    labelPlacement?: 'before' | 'after';
};

export type CheckboxProps = Readonly<
    CheckboxOnlyProps &
        CheckboxLabelProps &
        Omit<HTMLProps<HTMLInputElement>, keyof (CheckboxOnlyProps & CheckboxLabelProps)>
>;

export const CheckboxInternal = forwardRef<HTMLInputElement, CheckboxOnlyProps>(
    (
        {
            checked,
            defaultChecked,
            disabled,
            id,
            tabIndex,
            onChange,
            onFocus,
            onBlur,
            hidden,
            ...rest
        },
        forwardedRef
    ) => {
        const checkboxRef = useRef<HTMLInputElement>(null);
        const mergedRef = mergeRefs(checkboxRef, forwardedRef);

        useEffect(() => {
            if (checkboxRef.current) {
                checkboxRef.current.indeterminate = checked === 'indeterminate';
            }
        }, [checked]);

        const changeHandler = useMemo<ChangeEventHandler<HTMLInputElement> | undefined>(
            () => (e) => {
                onChange?.({
                    value: e.target.checked,
                    syntheticEvent: e,
                    nativeEvent: e.nativeEvent,
                });
            },
            [onChange]
        );

        return (
            <StyledCheckbox
                type="checkbox"
                checked={checked === true}
                defaultChecked={defaultChecked}
                $indeterminate={checked === 'indeterminate' ? 'true' : undefined}
                disabled={disabled}
                id={id}
                tabIndex={tabIndex}
                onChange={changeHandler}
                onFocus={onFocus}
                onBlur={onBlur}
                $hidden={hidden}
                ref={mergedRef}
                {...rest}
            />
        );
    }
);

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, labelPlacement, ...rest }, ref) => {
        if (!label) {
            return <CheckboxInternal {...rest} ref={ref} />;
        }
        if (labelPlacement === 'before') {
            return (
                <CheckboxLabel>
                    {label}
                    <CheckboxInternal {...rest} ref={ref} />
                </CheckboxLabel>
            );
        }
        return (
            <CheckboxLabel>
                <CheckboxInternal {...rest} ref={ref} />
                {label}
            </CheckboxLabel>
        );
    }
);
